import { Menu, Search, PanelLeftClose, PanelLeftOpen, PanelTopClose, PanelTopOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/shared/ui/IconButton';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { useShellStore } from '../model/shellStore';
import { BrandLogo } from './BrandLogo';
import { ThemeToggle } from './ThemeToggle';
import { ExchangeRateBadge } from './ExchangeRateBadge';
import { LanguageMenu } from './LanguageMenu';
import { UserMenu } from '../userMenu/UserMenu';
import { ConnectionLostBanner } from './ConnectionLostBanner';
import { ChatHeaderButton } from '@/features/chat';
import { NotificationBell } from '@/features/notifications';
import type { ReactNode } from 'react';

/**
 * Üst çubuk: menü düğmesi, logo, (top-nav modunda modül menüleri), kur, spotlight, dil, tema, kullanıcı.
 * Soldaki düğme menü konumuna göre değişir: sol menüde daralt/genişlet, üst menüde
 * "yalnız simge / adları göster" (TopNavBar sığmazsa zaten kendiliğinden simgeye düşer).
 */
export function AppHeader({ center }: { center?: ReactNode }) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const menuPosition = usePrefsStore((s) => s.prefs.menuPosition);
  const collapsed = usePrefsStore((s) => s.prefs.sidebarCollapsed);
  const topCompact = usePrefsStore((s) => s.prefs.topNavCompact);
  const setPrefs = usePrefsStore((s) => s.set);
  const setDrawer = useShellStore((s) => s.setDrawer);
  const setSpotlight = useShellStore((s) => s.setSpotlight);

  return (
    <header className="border-border bg-bg-elevated sticky top-0 z-30 border-b">
      <ConnectionLostBanner />
      <div className="flex h-14 items-center gap-2 px-3">
        {isMobile ? (
          <IconButton label={t('shell.menu')} onClick={() => setDrawer(true)}>
            <Menu className="h-5 w-5" />
          </IconButton>
        ) : menuPosition === 'sidebar' ? (
          <IconButton
            label={collapsed ? t('shell.expand') : t('shell.collapse')}
            onClick={() => setPrefs({ sidebarCollapsed: !collapsed })}
          >
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </IconButton>
        ) : menuPosition === 'top' ? (
          <IconButton
            label={topCompact ? t('shell.topNav.captions') : t('shell.topNav.iconsOnly')}
            onClick={() => setPrefs({ topNavCompact: !topCompact })}
          >
            {topCompact ? <PanelTopOpen className="h-5 w-5" /> : <PanelTopClose className="h-5 w-5" />}
          </IconButton>
        ) : null}
        <BrandLogo />
        <div className="min-w-0 flex-1">{center}</div>
        {/* Kur göstergesi aramanın SOLUNDA: sık bakılan ama tıklanmayan bir bilgi,
            araç düğmelerinin arasına girip onları kaydırmasın. Dar ekranda gizleniyor. */}
        {!isMobile ? <ExchangeRateBadge /> : null}
        {!isMobile ? (
          <IconButton label={t('shell.search')} onClick={() => setSpotlight(true)}>
            <Search className="h-5 w-5" />
          </IconButton>
        ) : null}
        <NotificationBell />
        <ChatHeaderButton />
        <LanguageMenu />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
